import React from "react";
import {Form, FormGroup, Stack, SubmitButton, TextInput} from "@unibuddy/patron";
import * as yup from "yup";
import {ErrorMessage, FormikHelpers} from "formik";
import {useTranslation} from "@unibuddy/intl";
import {TFunction} from "i18next";
import {useLocation} from "react-router-dom";

import {
    EMAIL_REQUIRED,
    FIRST_NAME_REQUIRED,
    LAST_NAME_REQUIRED,
    PASSWORD_MIN_CHAR,
    PASSWORD_REQUIRED,
    VALID_EMAIL,
} from "../../../../shared/Shared/validations/validationMessages";
import {PasswordInput} from "./PasswordInput";
import {AuthFormError} from "../../../../components/AuthFormError/AuthFormError";
import {useUniversity} from "../../UniversityProvider/UniversityProvider";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_EMAIL, INTL_SIGNUP} from "../../../../translations/login";
import {INTL_SIGN_UP_ERROR_MESSAGE} from "../../../../translations/signup";
import {NameFormField} from "./RegisterPageComponents/NameFormField";

export const PASSWORD_MINIMUM_LENGTH = 12;

type NewRegisterPage1FormProps = {
    handleSubmit: (
        firstName: string,
        lastName: string,
        email: string,
        password: string,
    ) => Promise<void>;
};

type FormValues = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
};

function makeValidationSchema(
    t: TFunction<[IntlNamespaces.SIGNUP, IntlNamespaces.SETTINGS, IntlNamespaces.LOGIN]>,
) {
    return {
        firstName: yup
            .string()
            .trim()
            .required(t("validations:firstNameRequired", FIRST_NAME_REQUIRED)),
        lastName: yup
            .string()
            .trim()
            .required(t("validations:lastNameRequired", LAST_NAME_REQUIRED)),
        email: yup
            .string()
            .email(t("validations:email", VALID_EMAIL))
            .required(t("validations:emailRequired", EMAIL_REQUIRED)),
        password: yup
            .string()
            .required(t("validations:passwordRequired", PASSWORD_REQUIRED))
            .min(
                PASSWORD_MINIMUM_LENGTH,
                t("validations:passwordMinChat", PASSWORD_MIN_CHAR(PASSWORD_MINIMUM_LENGTH), {
                    minChar: PASSWORD_MINIMUM_LENGTH,
                }),
            ),
    };
}

export function RegisterPage1Form({handleSubmit}: NewRegisterPage1FormProps) {
    const {t} = useTranslation([
        IntlNamespaces.SIGNUP,
        IntlNamespaces.SETTINGS,
        IntlNamespaces.LOGIN,
    ]);
    const {features} = useUniversity();
    const isEmailVerificationEnabled = features.get("enableEmailVerification");
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const firstNameQueryParam = searchParams.get("signup_firstname") ?? "";
    const lastNameQueryParam = searchParams.get("signup_lastname") ?? "";
    const emailQueryParam = searchParams.get("signup_email") ?? "";

    return (
        <Form
            initialValues={{
                firstName: decodeURIComponent(firstNameQueryParam),
                lastName: decodeURIComponent(lastNameQueryParam),
                email: decodeURIComponent(emailQueryParam),
                password: "",
                serverError: "",
            }}
            validationSchema={makeValidationSchema(t)}
            onSubmit={async (
                values: {
                    firstName: string;
                    lastName: string;
                    email: string;
                    password: string;
                },
                {setSubmitting, setFieldError}: FormikHelpers<FormValues>,
            ) => {
                setSubmitting(true);
                try {
                    await handleSubmit(
                        values.firstName,
                        values.lastName,
                        values.email,
                        values.password,
                    );
                } catch (error) {
                    setFieldError(
                        "serverError",
                        t(
                            `${IntlNamespaces.SIGNUP}:${INTL_SIGN_UP_ERROR_MESSAGE.key}`,
                            INTL_SIGN_UP_ERROR_MESSAGE.defaultValue,
                        ),
                    );
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {(props: {errors: {serverError: string}}) => {
                const {errors} = props;

                return (
                    <Stack space="medium-large">
                        <Stack space="medium">
                            <NameFormField />
                            <FormGroup
                                id="email"
                                label={t(
                                    `${IntlNamespaces.LOGIN}:${INTL_EMAIL.key}`,
                                    INTL_EMAIL.defaultValue,
                                )}
                                helpBottom={
                                    isEmailVerificationEnabled
                                        ? t(
                                              "emailVerificationMessage",
                                              "We'll send a verification link to this email address and use this to verify your account.",
                                          )
                                        : null
                                }
                            >
                                <TextInput
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    required
                                />
                            </FormGroup>
                            <PasswordInput name="password" required />
                        </Stack>
                        {errors.serverError && (
                            <ErrorMessage name="serverError" component={AuthFormError} />
                        )}
                        <SubmitButton color="university" block size="lg">
                            {t(
                                `${IntlNamespaces.LOGIN}:${INTL_SIGNUP.key}`,
                                INTL_SIGNUP.defaultValue,
                            )}
                        </SubmitButton>
                    </Stack>
                );
            }}
        </Form>
    );
}
